"use client";

import { useState, useEffect } from "react";
import { Heart, Link2, Check } from "lucide-react";

/**
 * Generates a stable seed-based "base like count" from a slug string.
 * Different articles get different starting numbers (80–220 range)
 * so it looks organic, not uniform.
 */
function seedLikes(slug: string): number {
  let hash = 0;
  for (let i = 0; i < slug.length; i++) {
    hash = (hash << 5) - hash + slug.charCodeAt(i);
    hash |= 0;
  }
  return 80 + Math.abs(hash % 141); // 80–220
}

export default function EngagementBar({
  slug,
  url,
  inline = false,
}: {
  slug: string;
  url: string;
  inline?: boolean;
}) {
  const base = seedLikes(slug);
  const storageKey = `liked_${slug}`;

  const [liked, setLiked] = useState(false);
  const [count, setCount] = useState(base);
  const [copied, setCopied] = useState(false);

  // Rehydrate from localStorage on mount
  useEffect(() => {
    if (localStorage.getItem(storageKey) === "1") {
      setLiked(true);
      setCount(base + 1);
    }
  }, [base, storageKey]);

  function handleLike() {
    if (liked) {
      // Unlike
      localStorage.removeItem(storageKey);
      setLiked(false);
      setCount(base);
    } else {
      // Like
      localStorage.setItem(storageKey, "1");
      setLiked(true);
      setCount(base + 1);
    }
  }

  async function handleShare() {
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // Fallback for older browsers
      const el = document.createElement("textarea");
      el.value = url;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div className={`flex items-center gap-2 ${inline ? "" : "border-y border-gray-200 py-4 my-8"}`}>
      {/* Like button */}
      <button
        onClick={handleLike}
        className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all
          ${
            liked
              ? "bg-[#EF2BA0] text-white hover:bg-[#d4258f]"
              : "bg-gray-100 text-[#3A3A3A] hover:bg-[#fce7f3] hover:text-[#EF2BA0]"
          }`}
        aria-label={liked ? "Unlike this article" : "Like this article"}
      >
        <Heart
          className={`w-4 h-4 transition-all ${liked ? "fill-white" : ""}`}
        />
        <span>{count.toLocaleString()} {liked ? "liked" : "likes"}</span>
      </button>

      {/* Share / copy link button */}
      <button
        onClick={handleShare}
        className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all
          ${
            copied
              ? "bg-green-100 text-green-700"
              : "bg-gray-100 text-[#3A3A3A] hover:bg-[#E6F4FF] hover:text-[#0094FF]"
          }`}
        aria-label="Copy link to this article"
      >
        {copied ? (
          <>
            <Check className="w-4 h-4" />
            <span>Copied!</span>
          </>
        ) : (
          <>
            <Link2 className="w-4 h-4" />
            <span>Share</span>
          </>
        )}
      </button>
    </div>
  );
}
